import { Link } from 'react-router-dom';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';
import { ReferencePackLicenseContent } from './ReferencePackLicenseContent';

const lastUpdated = 'May 20, 2026';

const summaryItems = [
  'LifeDrawing.live is a platform for live life drawing sessions, model bookings, recordings, reference packs, job listings, payments, and related artist/model/host communication.',
  'Life drawing may include nude or semi-nude modelling. You must be at least 18 to use the platform, attend sessions, model, buy reference packs, or upload content.',
  'Hosts are responsible for the sessions they publish. Models control whether they are listed, approved, invited, recorded, or included in reference packs. Artists own their own artwork, not the source media.',
  'Reference packs are licensed access, not ownership. Access can be withdrawn for model safety, privacy, consent, legal, payment, or policy reasons.',
];

const termSections = [
  {
    title: '1. Who these terms apply to',
    body: [
      'These terms apply when you browse, create an account, host an event, attend a session, model, apply for modelling work, buy or sell a reference pack, upload artwork or media, send messages, or otherwise use LifeDrawing.live.',
      'Different users have different responsibilities. Hosts create and run sessions. Models provide their time, likeness, pose work, and related media. Artists and attendees join sessions, make artwork, and may buy licensed reference materials. LifeDrawing.live provides the platform, payment flow, media tools, and safety controls.',
      'By using the platform, you agree to these terms, the privacy notice, and any additional terms shown at the point of purchase, event creation, model application, recording, upload, or reference-pack release.',
    ],
  },
  {
    title: '2. Adult-only service',
    body: [
      'The platform is for adults only. You must be 18 or older to create an account, join a live session, model, host, appear on camera, purchase content, receive payouts, upload artwork, or communicate through the platform.',
      'You must not invite, depict, upload, sell, or distribute content involving anyone under 18. If we believe a user, model, attendee, host, image, video, job listing, or session involves a minor, we may remove it, suspend accounts, preserve relevant records, and report the issue where appropriate.',
    ],
  },
  {
    title: '3. Accounts and identity',
    body: [
      'You are responsible for the accuracy of the information you provide and for activity on your account. Keep your login credentials secure and tell us promptly if you believe your account has been compromised.',
      'Model applications, directory listings, payout setup, and some host or buyer actions may require identity, profile, or payment information. We may decline, remove, or re-review a model profile, job listing, event, reference pack, or payout where we need to protect users, verify eligibility, prevent misuse, or comply with law or payment-provider rules.',
    ],
  },
  {
    title: '4. Hosts and event organizers',
    body: [
      'Hosts are responsible for describing sessions accurately, setting prices and payout terms, inviting or selecting models, managing guests, and running sessions in a respectful and lawful way.',
      'Hosts must obtain any permissions needed for their sessions, including permissions relating to models, tutors, venues, ticketing, recordings, publicity, and teaching materials. If a host invites someone to model directly, that invited model does not need platform approval merely to model for that event, but approval is still required for directory listing and responding to public model ads.',
      'Hosts must not misrepresent payment terms, expected nudity or pose requirements, session format, recording status, attendee access, or use of images and videos.',
    ],
  },
  {
    title: '5. Models, applications, and invitations',
    body: [
      'Models who want to be listed in the directory or apply to public job ads must complete the platform model application and be approved. Hosts may still invite a person directly to model for a specific event without that person being approved for the public directory or job board.',
      'Models are responsible for making truthful representations in applications, profiles, availability, modelling preferences, posing-space information, and communications with hosts.',
      'A model may ask us to remove or restrict profile content, reference packs, or media access where there is a safety, privacy, consent, legal, or dignity concern. We will consider the request in light of existing purchases, legal obligations, and the reference-pack withdrawal terms below.',
    ],
  },
  {
    title: '6. Live sessions, conduct, and safety',
    body: [
      'Life drawing is an art and teaching context, not a sexual service. Treat models, hosts, artists, and staff with respect. Do not harass, sexualize, shame, threaten, pressure, stalk, dox, discriminate against, or exploit another person.',
      'Do not record, screenshot, rebroadcast, scrape, or capture session video, audio, chat, poses, model images, reference media, or other participant content except through platform tools and only where the relevant permissions allow it.',
      'Follow host and model instructions about pose timing, camera use, chat, gallery use, privacy, and session participation. We may remove users from sessions or suspend accounts for unsafe, disruptive, exploitative, unlawful, or non-consensual conduct.',
    ],
  },
  {
    title: '7. Recordings, photos, and reference packs',
    body: [
      'Session recordings, source videos, high-resolution stills, camera feeds, pose metadata, and reference packs are sensitive model media. They may only be created, uploaded, reviewed, sold, downloaded, viewed, or shared through the controls provided by the platform and the terms shown for the event or pack.',
      'A reference pack purchase or grant gives licensed access only. It does not transfer ownership of the underlying photos, videos, recordings, model likeness, session metadata, platform software, or seller rights.',
      'Reference pack access may expire, be deleted, or be withdrawn. Buyers and attendees must stop using source media after access expires or is withdrawn, except that ordinary completed human-made artworks created before withdrawal remain allowed unless the buyer breached these terms or used the pack in a prohibited way.',
    ],
  },
  {
    title: '8. Artwork and user content',
    body: [
      'You keep ownership of artwork, profile text, uploaded images, messages, job posts, applications, and other content you create, subject to the rights you grant us and others through the platform.',
      'You grant LifeDrawing.live a limited licence to host, store, process, display, transmit, resize, secure, moderate, and otherwise use your content as needed to operate the platform, provide support, process payments, run events, send notifications, prevent abuse, and make the features you choose available to other users.',
      'You must have the rights needed to upload or publish content. Do not upload another person\'s private information, copyrighted work, image, recording, likeness, or confidential material unless you have permission or a clear lawful basis.',
    ],
  },
  {
    title: '9. Prohibited uses',
    body: [
      'You must not use the platform or platform media for unlawful activity, harassment, impersonation, non-consensual sexual content, pornography, deepfakes, face recognition, biometric identification, AI model training, scraping, stock media datasets, hate or extremist material, defamation, fraud, payment abuse, spam, malware, or attempts to bypass access controls.',
      'You must not sell, publish, share, lend, sublicense, mirror, leak, extract, or redistribute source videos, photos, reference-pack files, download links, screenshots, or substantially similar copies except where the platform expressly allows it.',
    ],
  },
  {
    title: '10. Payments, fees, refunds, and payouts',
    body: [
      'Payments may include tickets, tips, reference packs, recordings, guarantees, model fees, host revenue, platform fees, and payment-provider fees. Prices, currencies, access periods, and applicable fees should be shown before purchase or payout.',
      'Payments are processed by Stripe or another payment provider. You may need to accept that provider\'s terms, complete identity checks, and provide payout information before receiving money. We may delay, reverse, withhold, or refuse payments where required for chargebacks, refunds, fraud prevention, payment-provider rules, tax/accounting obligations, safety reviews, or legal compliance.',
      'Refund and cancellation rights depend on the type of purchase, the timing of cancellation, whether a session has happened, whether digital content access has started, the host\'s event terms, and applicable consumer law. Nothing in these terms removes mandatory consumer rights that cannot legally be waived.',
    ],
  },
  {
    title: '11. Moderation and enforcement',
    body: [
      'We may review, remove, restrict, or refuse content, events, model listings, job ads, applications, purchases, reference packs, recordings, messages, or accounts where we believe this is needed for safety, consent, privacy, legal compliance, payment integrity, platform reliability, or community trust.',
      'We may investigate reports, preserve evidence, contact affected users, disable downloads, revoke access, suspend accounts, or cooperate with law enforcement or payment providers where appropriate.',
    ],
  },
  {
    title: '12. Platform availability and changes',
    body: [
      'We aim to provide a reliable live-video and media platform, but live sessions depend on user devices, cameras, microphones, browsers, internet connections, third-party services, payment providers, and hosting infrastructure. The platform may be unavailable, interrupted, delayed, degraded, or changed from time to time.',
      'We may add, remove, or change features, technical requirements, pricing structures, access rules, upload limits, recording quality settings, or moderation systems. Where a change materially affects existing paid access, we will try to handle it fairly.',
    ],
  },
  {
    title: '13. Liability',
    body: [
      'The platform is provided with reasonable care, but we do not promise that every session, stream, upload, payment, message, recording, reference pack, or download will be uninterrupted, error-free, secure, or fit for every artistic, teaching, commercial, or technical purpose.',
      'We are not responsible for losses caused by user conduct, host decisions, model no-shows, attendee behaviour, inaccurate user content, third-party services, network conditions, device failures, or unauthorized use outside our reasonable control. Nothing in these terms limits liability where it would be unlawful to do so, including liability for fraud or for death or personal injury caused by negligence.',
    ],
  },
  {
    title: '14. Termination',
    body: [
      'You may stop using the platform at any time. You may ask us to close your account, subject to outstanding payments, legal obligations, dispute handling, fraud prevention, safety records, and existing licensed access.',
      'We may suspend or terminate access if you breach these terms, create risk for other users, misuse media, fail payment checks, violate payment-provider rules, or use the platform in a way that could harm LifeDrawing.live or the community.',
    ],
  },
  {
    title: '15. Governing law and disputes',
    body: [
      'Where legally permitted, these terms are governed by the laws of England and Wales, and disputes will be handled by the courts of England and Wales. If mandatory consumer laws in your country give you additional rights or require a different forum, those rights still apply.',
      'Please contact us first at events@lifedrawing.live so we can try to resolve issues directly, especially where safety, privacy, payment, or access to model media is involved.',
    ],
  },
];

export function TermsPage() {
  return (
    <div className="min-h-screen bg-bg pt-[88px]">
      <NavBar variant="light" />
      <main className="page-container py-12">
        <div className="mb-10">
          <div className="page-eyebrow">Terms</div>
          <h1 className="page-heading">LifeDrawing.live terms</h1>
          <p className="mt-3 text-sm text-text-faint">Last updated: {lastUpdated}</p>
          <div className="mt-5 flex flex-wrap gap-3 text-sm">
            <Link to="/privacy" className="border-b border-line pb-px text-text-dim hover:border-text hover:text-text">
              Privacy notice
            </Link>
            <Link to="/reference-pack-licenses" className="border-b border-line pb-px text-text-dim hover:border-text hover:text-text">
              Reference pack licenses
            </Link>
          </div>
        </div>

        <section className="mb-10 rounded-xl border border-line bg-panel p-6 shadow-sm">
          <h2 className="font-display text-2xl font-normal text-text">Plain-language summary.</h2>
          <ul className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            {summaryItems.map((item) => (
              <li key={item} className="rounded-lg border border-line-soft bg-white p-4 text-sm leading-6 text-text-dim">
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs leading-6 text-text-faint">
            This summary is for convenience. The full terms below are the operative terms.
          </p>
        </section>

        <div className="space-y-4">
          {termSections.map((section) => (
            <section key={section.title} className="rounded-xl border border-line bg-white p-5">
              <h2 className="font-display text-[22px] font-normal text-text">{section.title}</h2>
              <div className="mt-3 space-y-3 text-sm leading-7 text-text-dim">
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12">
          <ReferencePackLicenseContent />
        </div>
      </main>
      <SharedFooter />
    </div>
  );
}
